import { useMemo } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Box } from '@mui/material';
import { CrudListPage, type DataTableV2Column } from '@core/components';
import { useCrudList } from '@core/hooks/useCrudList';
import {
  type Fabric,
  deleteFabric,
  getAllFabrics,
  reorderFabrics,
} from '../../../api/fabric';
import { ColorSwatch } from '../../components/ColorSwatch';
import FabricDialog from '../../components/FabricDialog';
import { useAppContext } from '../../context/AppContext';

const Fabrics = () => {
  const { refreshContext } = useAppContext();
  const {
    items: fabrics,
    loading,
    dialogOpen,
    editingItem: editingFabric,
    deleteConfirm,
    load: loadFabrics,
    openCreate,
    closeDialog,
    cancelDelete,
    confirmDelete,
    getRowId,
    contextMenuItems,
    rowReorder,
  } = useCrudList<Fabric>({
    loadItems: getAllFabrics,
    deleteItem: (fabric) => deleteFabric(fabric.id),
    reorderItems: reorderFabrics,
    loadErrorMessage: 'Failed to load fabrics',
    deleteSuccessMessage: 'Fabric deleted',
    deleteErrorMessage: 'Failed to delete fabric',
    reorderErrorMessage: 'Failed to reorder fabrics',
    refreshContext,
  });

  const columns = useMemo<DataTableV2Column<Fabric>[]>(() => {
    const fabricUsageColumn = (
      field: keyof Fabric,
      headerName: string,
      subheaderName: string
    ): DataTableV2Column<Fabric> => ({
      field,
      headerName,
      headerAlign: 'center',
      subheaderName,
      align: 'center',
      width: 75,
      renderCell: ({ row }) =>
        row[field] ? <CheckIcon color="success" fontSize="small" /> : null,
    });
    const maxColorCount = fabrics.reduce(
      (max, fabric) => Math.max(max, fabric.colors.length),
      0
    );
    const colorsMinWidth = Math.max(180, maxColorCount * 24 + 50);
    return [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
      },
      fabricUsageColumn('isChasubleBody', 'Chasuble', 'Body'),
      fabricUsageColumn('isChasublePanel', 'Chasuble', 'Panel'),
      fabricUsageColumn('isAlbBody', 'Alb', 'Body'),
      fabricUsageColumn('isStolePanel', 'Stole', 'Panel'),
      fabricUsageColumn('isStolePatching', 'Stole', 'Patching'),
      fabricUsageColumn('isMitreBody', 'Mitre', 'Body'),
      fabricUsageColumn('isMitrePanel', 'Mitre', 'Panel'),
      fabricUsageColumn('isMitreLining', 'Mitre', 'Lining'),
      {
        field: 'colors',
        headerName: 'Colors',
        flex: 1,
        minWidth: colorsMinWidth,
        sortable: false,
        renderCell: ({ row }) => (
          <Box
            sx={{ display: 'flex', alignItems: 'center', m: '0 !important' }}
          >
            {row.colors.map((color) => (
              <Box key={color.id} title={color.name}>
                <ColorSwatch hexCode={color.hexCode} />
              </Box>
            ))}
          </Box>
        ),
      },
    ];
  }, [fabrics]);

  return (
    <CrudListPage<Fabric>
      variant="section"
      title="Fabrics"
      rows={fabrics}
      columns={columns}
      loading={loading}
      getRowId={getRowId}
      contextMenuItems={contextMenuItems}
      rowReorder={rowReorder}
      addConfig={{ label: 'Add Fabric', onAdd: openCreate }}
      addEditDialog={
        <FabricDialog
          open={dialogOpen}
          onClose={closeDialog}
          fabric={editingFabric}
          onSuccess={() => loadFabrics()}
        />
      }
      deleteConfig={{
        title: 'Delete fabric',
        item: deleteConfirm,
        getContent: (fabric) =>
          `Are you sure you want to delete "${fabric.name}"?`,
        onConfirm: confirmDelete,
        onCancel: cancelDelete,
      }}
    />
  );
};

export default Fabrics;
